/**
 * Infrastructure Intelligence — Response Planning  (Step 8a of the 8-step intelligence loop)
 *
 * Generate and dispatch enforcement responses for confirmed infrastructure incidents.
 * Actions: stop-work order, compliance notice, engineer inspection, emergency reinstatement.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  ClipboardList, Users, Scale, ShieldAlert,
  RefreshCw, ChevronDown, ChevronUp, ExternalLink,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Modal from '../../components/common/Modal';
import Alert from '../../components/common/Alert';
import api from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface IncidentMission {
  id: string;
  name: string;
  status: string;
  updated_at: string;
  metadata?: {
    step7_status?: string;
    step8_status?: string;
    asset_name?: string;
    diagnosis?: string;
    severity?: string;
    earthwork_delta_m3?: number;
    erp_work_order_id?: string;
    vertical?: string;
    [key: string]: any;
  };
}

interface ResponseForm {
  response_type:   string;
  officer_name:    string;
  estimated_eta_h: string;
  reference_no:    string;
  notes:           string;
}

const EMPTY_FORM: ResponseForm = {
  response_type:   'stop_work_order',
  officer_name:    '',
  estimated_eta_h: '24',
  reference_no:    '',
  notes:           '',
};

const RESPONSE_TYPES = [
  { value: 'stop_work_order',        label: 'Stop-Work Order' },
  { value: 'compliance_notice',      label: 'Compliance Notice' },
  { value: 'engineer_inspection',    label: 'Engineer Site Inspection' },
  { value: 'emergency_reinstate',    label: 'Emergency Reinstatement' },
  { value: 'legal_referral',         label: 'Refer to Legal / Regulator' },
];

const STATUS_STYLE: Record<string, string> = {
  confirmed_critical:   'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  confirmed_compliance: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  confirmed_monitor:    'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
};

const STATUS_LABELS: Record<string, string> = {
  confirmed_critical:   'Critical',
  confirmed_compliance: 'Compliance',
  confirmed_monitor:    'Monitor',
};

// ─── Component ────────────────────────────────────────────────────────────────

export const InfraResponsePlanningPage: React.FC = () => {
  const [missions, setMissions]     = useState<IncidentMission[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [success, setSuccess]       = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [planningId, setPlanningId] = useState<string | null>(null);
  const [form, setForm]             = useState<ResponseForm>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/missions?mission_type=infrastructure_survey&limit=50');
      const all: IncidentMission[] = res.data?.missions ?? [];
      setMissions(
        all.filter(
          (m) =>
            m.metadata?.vertical === 'infrastructure' &&
            (m.metadata?.step7_status === 'confirmed_critical' ||
             m.metadata?.step7_status === 'confirmed_compliance' ||
             m.metadata?.step7_status === 'confirmed_monitor') &&
            !m.metadata?.step8_status
        )
      );
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load incidents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDispatch = async (mission: IncidentMission) => {
    if (!form.officer_name.trim()) { setError('Responsible officer name is required'); return; }
    setSubmitting(true); setError(null);
    try {
      await api.patch(`/api/missions/${mission.id}`, {
        metadata: {
          ...mission.metadata,
          step8_status:         'dispatched',
          step8_response_type:  form.response_type,
          step8_officer:        form.officer_name.trim(),
          step8_eta_h:          Number(form.estimated_eta_h),
          step8_reference:      form.reference_no.trim(),
          step8_notes:          form.notes.trim(),
          step8_dispatched_at:  new Date().toISOString(),
        },
      });
      setSuccess(`Response dispatched for "${mission.name}"`);
      setPlanningId(null);
      setForm(EMPTY_FORM);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to dispatch response');
    } finally {
      setSubmitting(false);
    }
  };

  const planningMission = missions.find((m) => m.id === planningId) ?? null;

  const INPUT_CLS  = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';
  const SELECT_CLS = INPUT_CLS;

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <ClipboardList className="w-7 h-7 text-blue-500" /> Response Planning
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Step 8 — Issue stop-work orders, compliance notices, or engineer inspection orders
          </p>
        </div>
        <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
      </div>

      {error   && <Alert type="error"   onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ── Incident list ── */}
      <Card padding="none">
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {missions.length} incident{missions.length !== 1 ? 's' : ''} awaiting response
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-14"><Spinner size="lg" /></div>
        ) : missions.length === 0 ? (
          <div className="text-center py-14">
            <ShieldAlert className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No confirmed incidents pending response</p>
            <p className="text-sm text-gray-400 mt-1">Confirm findings in <strong>Analysis Review → Step 7</strong> first</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {missions.map((m) => {
              const isExpanded = expandedId === m.id;
              const statusKey  = m.metadata?.step7_status ?? '';
              return (
                <div key={m.id}>
                  <button
                    className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 text-left"
                    onClick={() => setExpandedId(isExpanded ? null : m.id)}
                  >
                    <Scale className="w-4 h-4 flex-shrink-0 text-blue-500" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{m.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{m.metadata?.asset_name ?? '—'}</p>
                    </div>
                    {statusKey && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[statusKey] ?? 'bg-gray-100 text-gray-700'}`}>
                        {STATUS_LABELS[statusKey] ?? statusKey}
                      </span>
                    )}
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                  </button>

                  {isExpanded && (
                    <div className="px-5 pb-5 pt-2 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700 space-y-3">
                      {m.metadata?.diagnosis && (
                        <p className="text-sm text-gray-700 dark:text-gray-300">{m.metadata.diagnosis}</p>
                      )}
                      {m.metadata?.earthwork_delta_m3 != null && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Earthwork delta: <strong className="text-gray-800 dark:text-gray-200">
                            {m.metadata.earthwork_delta_m3 > 0 ? '+' : ''}{Number(m.metadata.earthwork_delta_m3).toLocaleString()} m³
                          </strong>
                        </p>
                      )}
                      <Button size="sm" onClick={() => { setPlanningId(m.id); setForm(EMPTY_FORM); }}>
                        <ClipboardList className="w-4 h-4 mr-1" /> Plan Response
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* ── Response planning modal ── */}
      {planningMission && (
        <Modal onClose={() => setPlanningId(null)} panelClassName="max-w-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Response Plan</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{planningMission.name}</p>
              </div>
              <Scale className="w-5 h-5 text-blue-500" />
            </div>

            <div className="space-y-3 mb-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Response Type</label>
                <select className={SELECT_CLS} value={form.response_type}
                  onChange={(e) => setForm((p) => ({ ...p, response_type: e.target.value }))}>
                  {RESPONSE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Responsible Officer *</label>
                <input className={INPUT_CLS} placeholder="Full name / designation"
                  value={form.officer_name}
                  onChange={(e) => setForm((p) => ({ ...p, officer_name: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Response Deadline (hrs)</label>
                <input className={INPUT_CLS} type="number" min="1" max="720"
                  value={form.estimated_eta_h}
                  onChange={(e) => setForm((p) => ({ ...p, estimated_eta_h: e.target.value }))} />
              </div>

              <div className="rounded-lg bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 p-3 space-y-2">
                <p className="text-xs font-semibold text-blue-700 dark:text-blue-400 flex items-center gap-1">
                  <ExternalLink className="w-3.5 h-3.5" /> Reference / Notice Number (optional)
                </p>
                <input className={INPUT_CLS} placeholder="e.g. ENF-2026-0042 or project notice ref"
                  value={form.reference_no}
                  onChange={(e) => setForm((p) => ({ ...p, reference_no: e.target.value }))} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
                <textarea rows={2} className={INPUT_CLS}
                  placeholder="Site access requirements, contractor contact, legal jurisdiction…"
                  value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} />
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={() => handleDispatch(planningMission)} loading={submitting}
                disabled={!form.officer_name.trim() || submitting} className="flex-1">
                <Users className="w-4 h-4 mr-1" /> Dispatch Response
              </Button>
              <Button variant="outline" onClick={() => setPlanningId(null)} disabled={submitting} className="flex-1">
                Cancel
              </Button>
            </div>
        </Modal>
      )}
    </div>
  );
};

export default InfraResponsePlanningPage;

// Made with Bob
